import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Loader2,
  Sparkles,
  Zap,
  FileText,
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
  CheckCircle2,
  ArrowRight,
  Play,
} from "lucide-react";
import { toast } from "sonner";
import Outliner from "../../../designView/outliner/Outliner";
import { OutlineItem } from "../../../designView/outliner/types";
import { UserTakeaway } from "../../../../api/userTakeaways";
import {
  actionToOutline,
  createActivityFromOutline,
  quickGenerateActivity,
} from "../../../../api/actionItemAI";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { cn } from "../../../../lib/utils";
import { GeneratingLoader } from "../../../ui/generating-loader";

type CreationMode = "ai-outline" | "quick" | "blank";
type WizardStep = "choose-mode" | "edit-outline" | "configure" | "preview" | "creating" | "success";

interface ActionToActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionItem: UserTakeaway | null;
  onActivityCreated: () => void;
  /** Called right before the dialog closes itself to navigate away (Start Blank, Run, Open in design). */
  onLeave?: () => void;
}

const ActionToActivityDialog: React.FC<ActionToActivityDialogProps> = ({
  open,
  onOpenChange,
  actionItem,
  onActivityCreated,
  onLeave,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();

  // Wizard state
  const [step, setStep] = useState<WizardStep>("choose-mode");
  const [mode, setMode] = useState<CreationMode | null>(null);

  // Outline state
  const [outlineItems, setOutlineItems] = useState<OutlineItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isLoadingOutline, setIsLoadingOutline] = useState(false);

  // Configuration state
  const [activityName, setActivityName] = useState("");
  const [language, setLanguage] = useState("en");
  const [generationStyle, setGenerationStyle] = useState<"skeleton" | "full">("full");
  const [tone, setTone] = useState("encouraging");
  const [markComplete, setMarkComplete] = useState(false);
  const [openInDesign, setOpenInDesign] = useState(true);

  // Result state
  const [isCreating, setIsCreating] = useState(false);
  const [createdActivityId, setCreatedActivityId] = useState<string | null>(null);
  const [createdActivityName, setCreatedActivityName] = useState("");
  const [panesCreated, setPanesCreated] = useState(0);

  // Reset when dialog opens
  useEffect(() => {
    if (open && actionItem) {
      setStep("choose-mode");
      setMode(null);
      setOutlineItems([]);
      setSelectedItems([]);
      setActivityName("");
      setLanguage(i18n.language || "en");
      setGenerationStyle("full");
      setTone("encouraging");
      setMarkComplete(false);
      setOpenInDesign(true);
      setCreatedActivityId(null);
    }
  }, [open, actionItem?._id]);

  const generateId = () => `out_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Flatten outline items for selection
  const flattenItems = (items: OutlineItem[]): string[] => {
    return items.reduce((acc: string[], item) => {
      acc.push(item.id);
      if (item.children?.length) {
        acc.push(...flattenItems(item.children));
      }
      return acc;
    }, []);
  };

  const handleModeSelect = async (selectedMode: CreationMode) => {
    setMode(selectedMode);

    if (selectedMode === "blank") {
      // Navigate directly to DesignView with new activity
      onLeave?.();
      onOpenChange(false);
      const nameParam = actionItem?.content ? `&name=${encodeURIComponent(actionItem.content)}` : "";
      navigate(`/design?new=true${nameParam}`);
      return;
    }

    if (selectedMode === "quick") {
      // Quick generate - skip to creating
      setStep("creating");
      await handleQuickGenerate();
      return;
    }

    // AI outline mode - load suggestions
    setStep("edit-outline");
    await loadAISuggestions();
  };

  const loadAISuggestions = async () => {
    if (!actionItem) return;

    setIsLoadingOutline(true);
    try {
      const response = await actionToOutline(retryFetch, {
        actionItemId: actionItem._id,
        content: actionItem.content,
        category: actionItem.category?.name,
        language,
        subtasks: actionItem.childIds,
      });

      if (response.status === "SUCCESS") {
        setOutlineItems(response.outline);
        setActivityName(response.suggestedTitle);
        // Select all items by default
        setSelectedItems(flattenItems(response.outline));
      }
    } catch (error) {
      console.error("Failed to load AI suggestions:", error);
      toast.error(t("action_items.activity_wizard.ai_error", "Failed to generate outline"));
    } finally {
      setIsLoadingOutline(false);
    }
  };

  const handleQuickGenerate = async () => {
    if (!actionItem) return;

    setIsCreating(true);
    try {
      const response = await quickGenerateActivity(retryFetch, {
        actionItemId: actionItem._id,
        content: actionItem.content,
        language,
        settings: {
          tone,
          complexity: "moderate",
        },
      });

      if (response.status === "SUCCESS") {
        setCreatedActivityId(response.prescriptionId);
        setCreatedActivityName(response.prescriptionName);
        setPanesCreated(response.panesCreated);
        setStep("success");
        onActivityCreated();
        window.dispatchEvent(
          new CustomEvent("clary:celebration", {
            detail: { key: "first_activity_created" },
          })
        );
      } else {
        throw new Error(response.fault || "Unknown error");
      }
    } catch (error) {
      console.error("Failed to quick generate activity:", error);
      toast.error(t("action_items.activity_wizard.create_error", "Failed to create activity"));
      setStep("choose-mode");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateActivity = async () => {
    if (!actionItem || outlineItems.length === 0) return;

    setStep("creating");
    setIsCreating(true);

    try {
      // Filter outline to only include selected items
      const filterSelected = (items: OutlineItem[]): OutlineItem[] => {
        return items
          .filter((item) => selectedItems.includes(item.id))
          .map((item) => ({
            ...item,
            children: filterSelected(item.children),
          }));
      };

      const filteredOutline = filterSelected(outlineItems);

      const response = await createActivityFromOutline(retryFetch, {
        title: activityName || actionItem.content.substring(0, 50),
        outline: filteredOutline,
        sourceActionItemId: actionItem._id,
        settings: {
          language,
          tone,
          generateComponents: generationStyle === "full",
          markActionComplete: markComplete,
        },
      });

      if (response.status === "SUCCESS") {
        setCreatedActivityId(response.prescriptionId);
        setCreatedActivityName(response.prescriptionName);
        setPanesCreated(response.panesCreated);
        setStep("success");
        onActivityCreated();
        window.dispatchEvent(
          new CustomEvent("clary:celebration", {
            detail: { key: "first_activity_created" },
          })
        );
      } else {
        throw new Error(response.fault || "Unknown error");
      }
    } catch (error) {
      console.error("Failed to create activity:", error);
      toast.error(t("action_items.activity_wizard.create_error", "Failed to create activity"));
      setStep("configure");
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenInDesign = () => {
    if (createdActivityId) {
      onLeave?.();
      onOpenChange(false);
      // Open design mode with sketchbook visible and flagged as the user's plan
      navigate(`/design?wid=${createdActivityId}&openSketchbook=true&sketchbookIsPlan=true`);
    }
  };

  const handleRunActivity = () => {
    if (createdActivityId) {
      onLeave?.();
      onOpenChange(false);
      // Navigate to activity run page
      navigate(`/activities/run?wid=${createdActivityId}`);
    }
  };

  const handleAddOutlineItem = () => {
    const newItem: OutlineItem = {
      id: generateId(),
      text: "",
      children: [],
      expanded: true,
    };
    setOutlineItems([...outlineItems, newItem]);
    setSelectedItems([...selectedItems, newItem.id]);

    setTimeout(() => {
      const element = document.querySelector(`[data-item-id="${newItem.id}"]`) as HTMLElement;
      element?.focus();
    }, 50);
  };

  // Count selected top-level items (panes)
  const selectedPaneCount = outlineItems.filter((item) => selectedItems.includes(item.id)).length;

  if (!actionItem) return null;

  const renderStep = () => {
    switch (step) {
      case "choose-mode":
        return (
          <div className="py-4">
            {/* Action item context */}
            <div className="p-3 mb-6 rounded-lg bg-muted/50 border">
              <p className="text-sm font-medium line-clamp-2">{actionItem.content}</p>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              {t("action_items.activity_wizard.choose_mode", "How would you like to create your activity?")}
            </p>

            {/* Mode options */}
            <div className="space-y-3">
              {/* AI Outline */}
              <button
                className="w-full p-4 text-left rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors group"
                onClick={() => handleModeSelect("ai-outline")}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      {t("action_items.activity_wizard.ai_outline", "AI-Suggested Outline")}
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {t("recommended", "Recommended")}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        "action_items.activity_wizard.ai_outline_desc",
                        "AI creates an activity structure based on your action item. You can edit it before creating."
                      )}
                    </p>
                  </div>
                </div>
              </button>

              {/* Quick Generate */}
              <button
                className="w-full p-4 text-left rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors group"
                onClick={() => handleModeSelect("quick")}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <div className="font-semibold">
                      {t("action_items.activity_wizard.quick_generate", "Quick Generate")}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        "action_items.activity_wizard.quick_generate_desc",
                        "AI creates a complete activity ready to use. Best for simple action items."
                      )}
                    </p>
                  </div>
                </div>
              </button>

              {/* Start Blank */}
              <button
                className="w-full p-4 text-left rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors group"
                onClick={() => handleModeSelect("blank")}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <div className="font-semibold">
                      {t("action_items.activity_wizard.start_blank", "Start Blank")}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        "action_items.activity_wizard.start_blank_desc",
                        "Open DesignView with an empty canvas. Build your activity from scratch."
                      )}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        );

      case "edit-outline":
        return (
          <div className="py-4 flex flex-col min-h-0 max-h-[60vh]">
            {/* Help text - Requirement 1 */}
            <div className="mb-4 p-3 rounded-lg bg-primary/5 dark:bg-primary/20 border border-primary/20 dark:border-primary/80">
              <p className="text-xs text-primary dark:text-primary-foreground/80">
                <strong>{t("action_items.activity_wizard.outline_edit_help_title", "Customize Your Outline:")}</strong>{" "}
                {t(
                  "action_items.activity_wizard.outline_edit_help",
                  "Add, edit, remove, or reorder items. Uncheck items to exclude them. Top-level items become panes."
                )}
              </p>
            </div>

            {/* Activity name */}
            <div className="mb-4">
              <Label htmlFor="activity-name" className="text-sm mb-1.5 block">
                {t("action_items.activity_wizard.activity_name", "Activity Name")}
              </Label>
              <Input
                id="activity-name"
                value={activityName}
                onChange={(e) => setActivityName(e.target.value)}
                placeholder={t("action_items.activity_wizard.enter_name", "Enter activity name")}
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 mb-3">
              <Button variant="outline" size="sm" onClick={handleAddOutlineItem} className="gap-2">
                <Plus className="h-4 w-4" />
                {t("action_items.activity_wizard.add_item", "Add Item")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadAISuggestions}
                disabled={isLoadingOutline}
                className="gap-2"
              >
                {isLoadingOutline ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {t("action_items.activity_wizard.regenerate", "Regenerate")}
              </Button>
            </div>

            {/* Outliner */}
            <div className="flex-1 min-h-0 overflow-auto border rounded-lg p-2 bg-background">
              {isLoadingOutline ? (
                <div className="flex flex-col items-center justify-center h-full py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {t("action_items.activity_wizard.generating", "Generating outline...")}
                  </p>
                </div>
              ) : outlineItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-8 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mb-3 opacity-30" />
                  <p className="text-sm">{t("action_items.activity_wizard.no_outline", "No outline items yet")}</p>
                </div>
              ) : (
                <Outliner
                  data={outlineItems}
                  onChange={setOutlineItems}
                  readonly={false}
                  selectedItems={selectedItems}
                  onSelectionChange={setSelectedItems}
                />
              )}
            </div>

            {/* Info */}
            <p className="text-xs text-muted-foreground mt-3">
              <strong>{t("selected", "Selected")}:</strong> {selectedPaneCount} {t("panes", "panes")} •{" "}
              {t(
                "action_items.activity_wizard.outline_info",
                "Top-level items become activity panes. Nested items become content within those panes."
              )}
            </p>
          </div>
        );

      case "configure":
        return (
          <div className="py-4 space-y-5">
            {/* Language */}
            <div>
              <Label htmlFor="language" className="text-sm mb-1.5 block">
                {t("action_items.activity_wizard.language", "Language")}
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="he">עברית</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Generation Style */}
            <div>
              <Label className="text-sm mb-2 block">
                {t("action_items.activity_wizard.generation_style", "Content Type")}
              </Label>
              <RadioGroup value={generationStyle} onValueChange={(v) => setGenerationStyle(v as "skeleton" | "full")}>
                <div className="space-y-3">
                  {/* Plain text option */}
                  <label
                    htmlFor="skeleton"
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors",
                      generationStyle === "skeleton"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <RadioGroupItem value="skeleton" id="skeleton" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {t("action_items.activity_wizard.skeleton_title", "Simple Structure")}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t(
                          "action_items.activity_wizard.skeleton_desc",
                          "Plain text from your outline. Quick to create, easy to edit in Design mode."
                        )}
                      </p>
                    </div>
                  </label>

                  {/* Rich content option */}
                  <label
                    htmlFor="full"
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors",
                      generationStyle === "full"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <RadioGroupItem value="full" id="full" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {t("action_items.activity_wizard.full_title", "Rich Interactive Content")}
                        <Sparkles className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t(
                          "action_items.activity_wizard.full_desc",
                          "AI generates engaging components: text areas, checkboxes, sliders, and more. Takes longer."
                        )}
                      </p>
                    </div>
                  </label>
                </div>
              </RadioGroup>
            </div>

            {/* Tone */}
            <div>
              <Label htmlFor="tone" className="text-sm mb-1.5 block">
                {t("action_items.activity_wizard.tone", "Tone")}
              </Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger id="tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="encouraging">{t("tone_encouraging", "Encouraging")}</SelectItem>
                  <SelectItem value="calm">{t("tone_calm", "Calm")}</SelectItem>
                  <SelectItem value="professional">{t("tone_professional", "Professional")}</SelectItem>
                  <SelectItem value="friendly">{t("tone_friendly", "Friendly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Options */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="mark-complete"
                  checked={markComplete}
                  onCheckedChange={(checked) => setMarkComplete(checked === true)}
                />
                <Label htmlFor="mark-complete" className="font-normal cursor-pointer">
                  {t("action_items.activity_wizard.mark_complete", "Mark action item as completed after creation")}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="open-design"
                  checked={openInDesign}
                  onCheckedChange={(checked) => setOpenInDesign(checked === true)}
                />
                <Label htmlFor="open-design" className="font-normal cursor-pointer">
                  {t("action_items.activity_wizard.open_design", "Open activity in DesignView after creation")}
                </Label>
              </div>
            </div>
          </div>
        );

      case "preview":
        return (
          <div className="py-4">
            <div className="p-4 rounded-lg border bg-muted/30 mb-4">
              <h3 className="font-semibold text-lg mb-3">{activityName}</h3>

              <div className="space-y-2">
                {outlineItems
                  .filter((item) => selectedItems.includes(item.id))
                  .map((item, index) => (
                    <div key={item.id} className="p-3 rounded-md bg-background border">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                          {t("pane", "Pane")} {index + 1}
                        </span>
                        <span className="font-medium text-sm">{item.text}</span>
                      </div>
                      {item.children.filter((c) => selectedItems.includes(c.id)).length > 0 && (
                        <div className="pl-4 mt-2 space-y-1">
                          {item.children
                            .filter((child) => selectedItems.includes(child.id))
                            .map((child) => (
                              <p key={child.id} className="text-xs text-muted-foreground flex items-center gap-1">
                                <ArrowRight className="h-3 w-3" />
                                {child.text}
                              </p>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              {t("action_items.activity_wizard.summary", "{{panes}} panes", { panes: selectedPaneCount })}
              {" • "}
              {language === "en" ? "English" : language === "he" ? "עברית" : "Deutsch"}
            </p>
          </div>
        );

      case "creating":
        return (
          <div className="py-12 flex flex-col items-center justify-center">
            <GeneratingLoader context="activity" />
          </div>
        );

      case "success":
        return (
          <div className="py-8 flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {t("action_items.activity_wizard.activity_created", "Activity Created!")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t("action_items.activity_wizard.created_summary", '"{{name}}" with {{panes}} panes', {
                name: createdActivityName,
                panes: panesCreated,
              })}
            </p>

            <div className="flex flex-col gap-3 w-full max-w-sm">
              <div className="flex gap-3">
                <Button variant="default" onClick={handleRunActivity} className="flex-1 gap-2">
                  <Play className="h-4 w-4" />
                  {t("action_items.activity_wizard.run_activity_btn", "Run Activity")}
                </Button>
                <Button variant="outline" onClick={handleOpenInDesign} className="flex-1">
                  {t("action_items.activity_wizard.open_design_btn", "Open in Design")}
                </Button>
              </div>
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full">
                {t("close", "Close")}
              </Button>
            </div>
          </div>
        );
    }
  };

  const getStepNumber = () => {
    switch (step) {
      case "choose-mode":
        return 1;
      case "edit-outline":
        return 2;
      case "configure":
        return 3;
      case "preview":
        return 4;
      default:
        return 0;
    }
  };

  const showStepIndicator = ["edit-outline", "configure", "preview"].includes(step);
  const showFooter = ["edit-outline", "configure", "preview"].includes(step);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("sm:max-w-[600px]", step === "edit-outline" && "max-h-[85vh]")}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            {t("action_items.activity_wizard.title", "Create Activity from Action Item")}
          </DialogTitle>
          {showStepIndicator && (
            <div className="flex items-center gap-2 pt-2">
              {[1, 2, 3, 4].map((num) => (
                <div
                  key={num}
                  className={cn(
                    "h-2 flex-1 rounded-full transition-colors",
                    num <= getStepNumber() ? "bg-primary" : "bg-muted"
                  )}
                />
              ))}
            </div>
          )}
        </DialogHeader>

        {renderStep()}

        {showFooter && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (step === "edit-outline") setStep("choose-mode");
                else if (step === "configure") setStep("edit-outline");
                else if (step === "preview") setStep("configure");
              }}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {t("back", "Back")}
            </Button>

            {step === "edit-outline" && (
              <Button onClick={() => setStep("configure")} disabled={selectedPaneCount === 0}>
                {t("action_items.activity_wizard.next_configure", "Next: Configure")}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}

            {step === "configure" && (
              <Button onClick={() => setStep("preview")}>
                {t("action_items.activity_wizard.next_preview", "Next: Preview")}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}

            {step === "preview" && (
              <Button onClick={handleCreateActivity}>
                {t("action_items.activity_wizard.create_btn", "Create Activity")}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActionToActivityDialog;
